<script setup lang="ts">
import { AlertCircle, CheckCircle, AlertTriangle, Info } from 'lucide-vue-next'
import { cva } from 'class-variance-authority'
import { cn } from '@/lib/utils'

type AlertVariant = 'info' | 'success' | 'error' | 'warning'

const props = defineProps<{
  variant?: AlertVariant
  title?: string
  compact?: boolean
}>()

const alertVariants = cva('flex gap-2.5 rounded-sm border', {
  variants: {
    variant: {
      info: 'bg-[rgba(79,70,229,0.03)] border-[rgba(79,70,229,0.18)] text-[var(--color-ink)]',
      success: 'bg-[rgba(16,185,129,0.04)] border-[rgba(16,185,129,0.2)] text-[var(--color-ink)]',
      error: 'bg-[rgba(239,68,68,0.04)] border-[rgba(239,68,68,0.2)] text-[var(--color-ink)]',
      warning: 'bg-[rgba(245,158,11,0.05)] border-[rgba(245,158,11,0.22)] text-[var(--color-ink)]',
    },
    compact: {
      true: 'px-4 py-3',
      false: 'px-5 py-4',
    },
  },
  defaultVariants: {
    variant: 'info',
    compact: false,
  },
})

const icons = {
  info: Info,
  success: CheckCircle,
  error: AlertCircle,
  warning: AlertTriangle,
}
</script>

<template>
  <div :class="cn(alertVariants({ variant: props.variant, compact: props.compact }))">
    <component :is="icons[props.variant ?? 'info']" class="mt-0.5 h-5 w-5 flex-shrink-0" />
    <div class="flex-1">
      <p v-if="props.title" class="mb-0.5 font-semibold text-[0.95rem] text-[var(--color-ink)]">{{ props.title }}</p>
      <div class="text-sm text-[var(--color-ink-soft)]">
        <slot />
      </div>
    </div>
  </div>
</template>
